import './IntegrationSettings.scss'
import { Col, Container, Row } from 'react-bootstrap'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { ScrollNavigation, ScrollNavigationSection } from '../../components/NavigationWithSideBar/ScrollNavigation'
import { IntegrationCard, IntegrationCardProps } from '../../components/Integrations/IntegrationCard/IntegrationCard'
import { getIntegrationCategoryDisplayName, getIntegrationServiceIdsForCategory, IntegrationCategory, IntegrationServiceId } from '../../common/integrations'
import React, { useMemo } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetMeQuery } from '../../store/user/user'
import { useIntegrations } from '../../apps/professional-app/fa/common/hooks/useIntegrations'

interface IntegrationSectionConfig {
  id: string
  label: string
  title?: string
  integrations: Array<IntegrationCardProps>
}

const createIntegrationSection = ({ id, label, title, integrations }: IntegrationSectionConfig): ScrollNavigationSection => {
  const Content: React.FC = () => (
    <Container fluid className='px-0 pb-5'>
      <Row className='g-3'>
        {integrations.map((integration, index) => (
          <Col key={index} xs={12} sm={6} xxl={4} className='d-flex justify-content-center integration-settings__integration-cards'>
            <IntegrationCard serviceId={integration.serviceId} />
          </Col>
        ))}
      </Row>
    </Container>
  )

  return {
    id,
    label,
    title,
    content: Content,
  }
}

export const IntegrationSettings: React.FC = () => {
  useDocumentTitle('Integrations')

  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })

  const { isIntegrationAllowed, isIntegrationCategoryAllowed } = useIntegrations()

  const integrationCards: IntegrationCardProps[] = useMemo(() => {
    return Object.values(IntegrationServiceId)
      .filter((serviceId) => isIntegrationAllowed(serviceId))
      .map((serviceId) => ({ serviceId }))
  }, [isIntegrationAllowed])

  const getIntegrationsByCategory = useMemo(
    () =>
      (category: IntegrationCategory): IntegrationCardProps[] => {
        return integrationCards.filter((ic) => getIntegrationServiceIdsForCategory(category).includes(ic.serviceId as IntegrationServiceId))
      },
    [integrationCards],
  )

  const sections: ScrollNavigationSection[] = useMemo(() => {
    return Object.values(IntegrationCategory)
      .filter((category) => isIntegrationCategoryAllowed(category))
      .map((category) =>
        createIntegrationSection({
          id: category,
          label: getIntegrationCategoryDisplayName(category),
          integrations: getIntegrationsByCategory(category),
        }),
      )
  }, [isIntegrationCategoryAllowed, getIntegrationsByCategory])

  const connectedIntegrations = useMemo(() => {
    return userData?.integrations?.map((i) => i.serviceId as IntegrationServiceId) || []
  }, [userData])

  const connectedIntegrationCards: IntegrationCardProps[] = useMemo(() => {
    return integrationCards.filter((ic) => connectedIntegrations.includes(ic.serviceId as IntegrationServiceId))
  }, [connectedIntegrations, integrationCards])

  const connectedSection = useMemo(() => {
    return createIntegrationSection({
      id: 'connected',
      label: 'Connected',
      integrations: connectedIntegrationCards,
    })
  }, [connectedIntegrationCards])
  return (
    <div className='full-size-content'>
      <Container fluid>
        <Row className='mb-9'>
          <Col className='p-0'>
            <h3>Integrations</h3>
          </Col>
        </Row>
        <Row>
          <Col lg={12} className='p-0'>
            <ScrollNavigation
              sections={[connectedSection, ...sections]}
              defaultActiveSectionId='connected'
              sectionClassName='integration-settings__integrations-container'
            />
          </Col>
        </Row>
      </Container>
    </div>
  )
}
